import React, { lazy, Suspense } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';

/**
 * Higher-order component for lazy loading with consistent loading state
 * @param {Function} importFunc - Dynamic import function
 * @param {Object} fallback - Optional custom fallback component
 * @returns {React.Component} Lazy loaded component with suspense
 */
export const lazyLoad = (importFunc, fallback = null) => {
  const LazyComponent = lazy(importFunc);
  
  const WrappedComponent = (props) => (
    <Suspense 
      fallback={
        fallback || (
          <div className="flex items-center justify-center min-h-screen">
            <LoadingSpinner size="lg" message="Loading..." />
          </div>
        )
      }
    >
      <LazyComponent {...props} />
    </Suspense>
  );
  WrappedComponent.displayName = 'LazyLoadedComponent';
  return WrappedComponent;
};

/**
 * Preload a lazy component for better UX
 * @param {Function} importFunc - Dynamic import function
 */
export const preloadComponent = (importFunc) => {
  importFunc();
};

/**
 * Create a lazy component with preloading capability
 * @param {Function} importFunc - Dynamic import function
 * @returns {Object} Object with component and preload function
 */
export const createLazyComponent = (importFunc) => {
  const component = lazyLoad(importFunc);
  const preload = () => preloadComponent(importFunc);
  
  return { component, preload };
};